:root {
  color-scheme: light;
  font-family:
    Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #141713;
  background: #f6f7f2;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  --ink: #141713;
  --muted: #667064;
  --paper: #ffffff;
  --wash: #f6f7f2;
  --line: #dfe4dc;
  --green: #14795a;
  --green-bg: #e3f2eb;
  --red: #b33f39;
  --red-bg: #f8e3df;
  --blue: #2459a8;
  --gold: #9f6a16;
  --shadow: 0 18px 42px rgba(20, 23, 19, 0.07);
}

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  margin: 0;
  min-width: 320px;
  background: var(--wash);
}

button,
input {
  font: inherit;
}

button {
  cursor: pointer;
}

a {
  color: inherit;
  text-decoration: none;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 58px;
  padding: 0 max(18px, calc((100vw - 1180px) / 2));
  border-bottom: 1px solid rgba(223, 228, 220, 0.92);
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(14px);
}

.brand,
.topbar-meta,
.eyebrow,
.source-note,
.source-link,
.chip-title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.brand {
  font-size: 18px;
  font-weight: 900;
}

.brand svg {
  color: var(--green);
}

.topbar-meta {
  color: var(--muted);
  font-size: 13px;
}

.hero,
.toolbar,
.watch-panel,
.profiles-section,
.sources {
  width: min(1180px, calc(100% - 40px));
  margin: 0 auto;
}

.hero {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 22px;
  padding: 34px 0 24px;
  align-items: stretch;
}

.hero-main,
.hero-side,
.watch-panel,
.profile-card,
.sources {
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper);
  box-shadow: var(--shadow);
}

.hero-main {
  padding: 26px;
}

.eyebrow,
.section-kicker {
  color: var(--blue);
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0;
  text-transform: uppercase;
}

.hero h1 {
  max-width: 760px;
  margin: 12px 0 18px;
  font-size: clamp(36px, 6vw, 68px);
  line-height: 1;
  letter-spacing: 0;
}

.market-tabs {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  max-width: 520px;
  margin: 0 0 18px;
}

.market-tabs button {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 44px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #f7f9f5;
  color: var(--ink);
  font-weight: 850;
}

.market-tabs button b {
  color: var(--muted);
  font-size: 12px;
}

.market-tabs button.active {
  border-color: var(--ink);
  background: var(--ink);
  color: #fff;
}

.market-tabs button.active b {
  color: #dfe6dd;
}

.name-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.name-strip span,
.tier,
.profile-tags span {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 9px;
  border-radius: 999px;
  background: #eef2ec;
  color: #465047;
  font-size: 12px;
  font-weight: 800;
}

.hero-pulse {
  display: grid;
  grid-template-columns: 178px minmax(0, 1fr);
  gap: 14px;
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid var(--line);
}

.pulse-score {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}

.pulse-score span {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  min-height: 34px;
  padding: 0 10px;
  border-radius: 7px;
  background: #f2f5f0;
  color: var(--muted);
  font-size: 13px;
  font-weight: 850;
}

.pulse-score span:first-child svg,
.pulse-feed .positive {
  color: var(--green);
}

.pulse-score span:last-child svg,
.pulse-feed .negative {
  color: var(--red);
}

.pulse-feed {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
  min-width: 0;
}

.pulse-feed a,
.pulse-empty {
  min-width: 0;
  min-height: 76px;
  padding: 10px;
  border-radius: 7px;
  background: #f7f9f5;
}

.pulse-feed a {
  display: grid;
  align-content: space-between;
  gap: 5px;
}

.pulse-feed b,
.pulse-feed small {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.pulse-feed b {
  font-size: 17px;
}

.pulse-feed span,
.pulse-feed small,
.pulse-empty {
  color: var(--muted);
  font-size: 12px;
  font-weight: 800;
}

.pulse-empty {
  display: flex;
  align-items: center;
  grid-column: 1 / -1;
}

.hero-side {
  display: grid;
  gap: 10px;
  padding: 18px;
}

.metric {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 72px;
  padding: 14px;
  border-radius: 8px;
  background: #f7f9f5;
}

.metric span {
  color: var(--muted);
  font-size: 13px;
  font-weight: 750;
}

.metric strong {
  font-size: 30px;
  line-height: 1;
}

.toolbar,
.section-title-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  margin-top: 18px;
  margin-bottom: 12px;
}

.toolbar h2,
.section-title-row h2,
.source-copy h2 {
  margin: 4px 0 0;
  font-size: clamp(24px, 4vw, 38px);
  line-height: 1.08;
  letter-spacing: 0;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  width: min(300px, 100%);
  min-height: 40px;
  padding: 0 12px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: var(--paper);
}

.search-box svg {
  color: var(--muted);
  flex: 0 0 auto;
}

.search-box input {
  width: 100%;
  border: 0;
  outline: 0;
  background: transparent;
}

.watch-panel {
  padding: 8px;
}

.move-list {
  display: grid;
  gap: 6px;
}

.move-row {
  display: grid;
  grid-template-columns: 34px minmax(70px, 0.72fr) minmax(120px, 1.4fr) minmax(92px, 0.7fr) 64px 18px;
  gap: 10px;
  align-items: center;
  min-height: 48px;
  padding: 6px 10px;
  border-radius: 7px;
}

.move-row:hover {
  background: #f5f7f2;
}

.direction-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 900;
}

.direction-badge.positive,
.positive-text {
  color: var(--green);
}

.direction-badge.positive {
  background: var(--green-bg);
}

.direction-badge.negative,
.negative-text {
  color: var(--red);
}

.direction-badge.negative {
  background: var(--red-bg);
}

.move-row strong {
  font-size: 16px;
  overflow-wrap: anywhere;
}

.move-person {
  min-width: 0;
  color: var(--ink);
  font-weight: 750;
}

.move-person small {
  display: block;
  margin-top: 2px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 650;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.move-mobile-meta {
  display: none;
}

.move-value,
.move-date,
.freshness {
  color: var(--muted);
  font-size: 13px;
  font-weight: 750;
}

.move-value {
  color: var(--ink);
  text-align: right;
}

.move-row svg {
  color: var(--muted);
}

.profiles-section {
  padding: 28px 0 44px;
}

.profile-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.profile-card {
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 286px;
  padding: 16px;
}

.profile-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.profile-card h3 {
  margin: 0;
  font-size: 19px;
  line-height: 1.18;
}

.profile-card p {
  margin: 5px 0 0;
  color: var(--muted);
  font-size: 13px;
  line-height: 1.35;
}

.tier {
  flex: 0 0 auto;
  background: #eef3f7;
  color: var(--blue);
}

.profile-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin: 12px 0 10px;
}

.profile-scan {
  display: grid;
  gap: 10px;
}

.return-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.return-row div {
  min-height: 54px;
  padding: 10px;
  border: 1px solid var(--line);
  border-radius: 8px;
  background: #fbfcfa;
}

.return-row span,
.return-source {
  display: block;
  color: var(--muted);
  font-size: 12px;
}

.return-row strong {
  display: block;
  margin-top: 5px;
  font-size: 19px;
}

.return-source {
  min-height: 18px;
  margin: 8px 0 0;
  line-height: 1.35;
}

.move-stack {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(132px, 1fr));
  gap: 8px;
}

.move-chips {
  min-width: 0;
}

.chip-title {
  margin-bottom: 6px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 850;
}

.move-chips.positive .chip-title svg {
  color: var(--green);
}

.move-chips.negative .chip-title svg {
  color: var(--red);
}

.move-chips div {
  display: grid;
  gap: 5px;
}

.move-chips a {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 0;
  min-height: 27px;
  padding: 0 8px;
  border-radius: 7px;
  background: #f2f5f0;
  font-size: 12px;
  font-weight: 850;
}

.move-chips.positive a {
  background: var(--green-bg);
}

.move-chips.negative a {
  background: var(--red-bg);
}

.move-chips a span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.move-chips small {
  color: var(--muted);
  flex: 0 0 auto;
  font-weight: 750;
}

.pending-note,
.empty-state {
  color: var(--muted);
  font-size: 13px;
  line-height: 1.5;
}

.pending-note {
  margin-top: auto;
  padding-top: 14px;
}

.empty-state {
  min-height: 130px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
}

.sources {
  display: grid;
  grid-template-columns: minmax(0, 0.78fr) minmax(320px, 1fr);
  gap: 28px;
  margin-bottom: 44px;
  padding: 22px;
  background: #18201b;
  color: #f8fbf7;
}

.source-copy p {
  color: #d6dfd4;
  line-height: 1.7;
}

.source-list {
  display: grid;
  gap: 8px;
}

.source-note,
.source-link {
  min-height: 42px;
  padding: 10px 12px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.07);
  color: #eef6ec;
  font-size: 13px;
  line-height: 1.45;
}

.source-note svg {
  flex: 0 0 auto;
  color: #78d9ac;
}

.source-link {
  justify-content: space-between;
  font-weight: 800;
}

.source-link svg {
  color: #aee4cb;
}

.loading-shell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 100vh;
  color: var(--muted);
  font-weight: 850;
}

.loading-shell.error {
  color: var(--red);
}

.spin {
  animation: spin 1.1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 980px) {
  .hero {
    grid-template-columns: 1fr;
  }

  .hero-side {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .profile-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sources {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 720px) {
  .hero,
  .toolbar,
  .watch-panel,
  .profiles-section,
  .sources {
    width: min(100% - 28px, 1180px);
  }

  .topbar {
    padding: 0 14px;
  }

  .topbar-meta {
    display: none;
  }

  .hero {
    padding-top: 20px;
  }

  .hero-main {
    padding: 18px;
  }

  .hero h1 {
    font-size: 36px;
  }

  .market-tabs {
    max-width: none;
  }

  .hero-pulse {
    grid-template-columns: 1fr;
  }

  .pulse-score,
  .pulse-feed {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .pulse-feed a,
  .pulse-empty {
    min-height: 62px;
  }

  .hero-side,
  .profile-grid {
    grid-template-columns: 1fr;
  }

  .toolbar,
  .section-title-row {
    align-items: stretch;
    flex-direction: column;
  }

  .search-box {
    width: 100%;
  }

  .move-row {
    grid-template-columns: 32px minmax(56px, 0.52fr) minmax(112px, 1fr) 18px;
    min-height: 54px;
  }

  .move-source {
    display: none !important;
  }

  .move-mobile-meta {
    display: block;
  }

  .move-value,
  .move-date {
    display: none;
  }

  .move-stack {
    grid-template-columns: 1fr;
  }

  .profile-card {
    min-height: auto;
  }
}
